import React, { createContext, useCallback, useContext, useEffect } from "react";
import { BaseThemeProps } from "@/base/components/theme/BaseThemeProvider";
import { useTheme as useThemeOriginal } from "nextjs-themes-ultralite/dist/hooks/use-theme";
import { getThemes } from "@/base/utils/theme/theme.util";
import { GetStaticPropsContext } from "next/types";
import { validateTheme } from "@/base/pages/_app";
import DesignConfig from "@/theme/design.config";

type Context = BaseThemeProps;

const Context = createContext({} as Context);

export const useTheme = (): Context => {
    const themeContext = useContext(Context);

    useEffect(() => {
        console.log("theme changed", themeContext.theme);
    }, [themeContext.theme]);

    return {
        ...themeContext,
        theme: themeContext.theme ?? themeContext.defaultTheme,
    };
};

export const getThemeSSRValue = (ctx: GetStaticPropsContext | any): string | null => {
    return ctx.req?.cookies?.[process.env.NEXT_PUBLIC_THEME_COOKIE_NAME || "sp-theme"] || null;
};

export const Provider = (props: { children: React.ReactNode } & Context & { theme: string | null }) => {
    const defaultTheme = process.env.NEXT_PUBLIC_DEFAULT_THEME || "light";
    const cookieName = process.env.NEXT_PUBLIC_THEME_COOKIE_NAME || "sp-theme";
    const ctx = useThemeOriginal();
    const themes = getThemes();
    const theme = validateTheme(ctx.theme || (props.theme as string) || defaultTheme) || defaultTheme;
    const [firstChange, setFirstChange] = React.useState(false);
    const [isSaved, setIsSaved] = React.useState(props.theme != null);

    const changeDataTheme = useCallback((theme: string) => {
        document.documentElement.setAttribute("data-theme", theme);

        for (const key of themes) {
            if (theme !== key) {
                document.documentElement.classList.remove(`${key}`);
            }
        }

        document.documentElement.classList.add(`${theme}`);

        if (!firstChange) {
            setFirstChange(true);
        }
    }, []);

    const saveCookie = useCallback((theme: string) => {
        document.cookie = `${cookieName}=${theme}; path=/; max-age=31536000`;
        setIsSaved(true);
    }, []);

    const setTheme = (theme?: string, save: boolean = true) => {
        ctx.setTheme(theme ?? defaultTheme);
        if (save) {
            saveCookie(theme ?? defaultTheme);
        }
    };

    useEffect(() => {
        changeDataTheme(theme);
    }, [theme]);

    useEffect(() => {
        const media = matchMedia("(prefers-color-scheme: dark)");
        const updateSystemColorScheme = (force: boolean) => {
            const xTheme = validateTheme(media.matches ? "dark" : "light");
            if (xTheme !== theme && (!isSaved || force)) {
                setTheme(xTheme, isSaved && force);
            }
        };
        updateSystemColorScheme(false);
        media.addEventListener("change", () => updateSystemColorScheme(true));
        return () => {
            media.removeEventListener("change", () => updateSystemColorScheme(false));
        };
    }, []);

    return (
        <Context.Provider
            value={{
                setTheme,
                defaultTheme,
                themes,
                theme,
            }}
        >
            <style jsx global>{`
                :root {
                    --sat: env(safe-area-inset-top);
                    --sar: env(safe-area-inset-right);
                    --sab: env(safe-area-inset-bottom);
                    --sal: env(safe-area-inset-left);
                }

                html {
                    background: ${DesignConfig[theme]?.baseBgColor};
                    height: 100%;
                    padding: env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left);
                }

                body {
                    opacity: ${firstChange ? 1 : 0};
                    background: ${DesignConfig[theme]?.baseBgColor};
                    min-height: 100%;
                    margin: 0;
                    padding: 0;
                }

                #__next {
                    min-height: 100vh;
                    /* Fallback for browsers that do not support Custom Properties */
                    min-height: calc(100vh + env(safe-area-inset-top));
                    margin: 0;
                    padding: 0;
                    background: ${DesignConfig[theme]?.baseBgColor};
                }

                @supports (padding: env(safe-area-inset-top)) {
                    #__next {
                        padding-top: env(safe-area-inset-top);
                        padding-right: env(safe-area-inset-right);
                        padding-bottom: env(safe-area-inset-bottom);
                        padding-left: env(safe-area-inset-left);
                    }
                }
            `}</style>
            <div data-theme={theme}>{props.children}</div>
        </Context.Provider>
    );
};
